import React from 'react';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { downloadCampaignImage, getActiveCampaignsForCommunity } from '@/services/campaignService';
import type { CommunityCampaign } from '@/types/campaigns';
import { ChevronLeft, ChevronRight, Loader2, Megaphone, X } from 'lucide-react';
import { useOnboardingTour } from '@/contexts/OnboardingTourContext';
import { useCommunityScope } from '@/hooks/useCommunityScope';
import { useNavigate } from 'react-router-dom';

export const ActiveCampaignModal: React.FC = () => {
  const { currentUser, isLoading: isAuthLoading } = useAuth();
  const [campaigns, setCampaigns] = React.useState<CommunityCampaign[]>([]);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [isFetching, setIsFetching] = React.useState(false);
  const [campaignImageUrl, setCampaignImageUrl] = React.useState<string | null>(null);
  const [campaignImageDimensions, setCampaignImageDimensions] = React.useState<{ width: number; height: number } | null>(null);
  const imageUrlRef = React.useRef<string | null>(null);
  const lastImagePathRef = React.useRef<string | null>(null);
  const { isOpen: isTourOpen, hasCompleted: hasCompletedTour } = useOnboardingTour();
  const navigate = useNavigate();
  const totalCampaigns = campaigns.length;
  const safeIndex = React.useMemo(() => {
    if (totalCampaigns === 0) {
      return 0;
    }

    if (currentIndex < 0 || currentIndex >= totalCampaigns) {
      return 0;
    }

    return currentIndex;
  }, [currentIndex, totalCampaigns]);
  const currentCampaign = totalCampaigns > 0 ? campaigns[safeIndex] ?? null : null;
  const isOpen = totalCampaigns > 0 && Boolean(currentCampaign);

  const revokeObjectUrl = React.useCallback(() => {
    const currentUrl = imageUrlRef.current;
    if (currentUrl) {
      URL.revokeObjectURL(currentUrl);
      imageUrlRef.current = null;
    }
  }, []);

  const resetImagePreview = React.useCallback(() => {
    revokeObjectUrl();
    lastImagePathRef.current = null;
    setCampaignImageUrl(null);
  }, [revokeObjectUrl]);

  const { activeCommunityId } = useCommunityScope();
  const communityId = activeCommunityId;
  const userId = currentUser?.id ?? null;

  React.useEffect(() => {
    setCampaigns([]);
    setCurrentIndex(0);
    resetImagePreview();
  }, [communityId, resetImagePreview, userId]);

  const handleClose = React.useCallback(() => {
    setCampaigns([]);
    setCurrentIndex(0);
    resetImagePreview();
    setCampaignImageDimensions(null);
  }, [resetImagePreview]);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        handleClose();
      }
    },
    [handleClose]
  );

  React.useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    const sanitizedCommunityId = communityId?.trim();

    if (!sanitizedCommunityId || !hasCompletedTour || isTourOpen) {
      setCampaigns([]);
      setCurrentIndex(0);
      resetImagePreview();
      setIsFetching(false);
      return;
    }

    let isMounted = true;
    setIsFetching(true);

    getActiveCampaignsForCommunity(sanitizedCommunityId)
      .then(activeCampaigns => {
        if (!isMounted) {
          return;
        }

        if (activeCampaigns.length > 0) {
          setCampaigns(activeCampaigns);
          setCurrentIndex(0);
        } else {
          setCampaigns([]);
          setCurrentIndex(0);
          resetImagePreview();
        }
      })
      .catch(error => {
        if (!isMounted) {
          return;
        }
        console.warn('No se pudo obtener la campaña activa de la comunidad.', error);
        setCampaigns([]);
        setCurrentIndex(0);
        resetImagePreview();
      })
      .finally(() => {
        if (!isMounted) {
          return;
        }
        setIsFetching(false);
      });

    return () => {
      isMounted = false;
    };
  }, [communityId, hasCompletedTour, isAuthLoading, isTourOpen, resetImagePreview, userId]);

  React.useEffect(() => {
    if (!currentCampaign || !currentCampaign.imagePath || !isOpen) {
      resetImagePreview();
      setCampaignImageDimensions(null);
      return;
    }

    if (lastImagePathRef.current === currentCampaign.imagePath && imageUrlRef.current) {
      return;
    }

    let isMounted = true;
    const imagePath = currentCampaign.imagePath;

    const fetchImage = async () => {
      try {
        const blob = await downloadCampaignImage(imagePath);
        const objectUrl = URL.createObjectURL(blob);

        if (!isMounted) {
          URL.revokeObjectURL(objectUrl);
          return;
        }

        revokeObjectUrl();
        imageUrlRef.current = objectUrl;
        setCampaignImageUrl(objectUrl);
        lastImagePathRef.current = imagePath;
        setCampaignImageDimensions(null);
      } catch (error) {
        if (!isMounted) {
          return;
        }
        console.warn('No se pudo descargar la imagen de la campaña activa.', error);
        resetImagePreview();
        setCampaignImageDimensions(null);
      }
    };

    void fetchImage();

    return () => {
      isMounted = false;
    };
  }, [currentCampaign, isOpen, resetImagePreview, revokeObjectUrl]);

  React.useEffect(() => {
    return () => {
      revokeObjectUrl();
    };
  }, [revokeObjectUrl]);

  React.useEffect(() => {
    if (!campaignImageUrl) {
      setCampaignImageDimensions(null);
    }
  }, [campaignImageUrl]);

  const campaignImageAspectRatio = React.useMemo(() => {
    if (!campaignImageDimensions || campaignImageDimensions.height === 0) {
      return null;
    }

    return campaignImageDimensions.width / campaignImageDimensions.height;
  }, [campaignImageDimensions]);

  const handleCampaignImageLoad = React.useCallback(
    (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
      const { naturalWidth, naturalHeight } = event.currentTarget;

      if (naturalWidth > 0 && naturalHeight > 0) {
        setCampaignImageDimensions({ width: naturalWidth, height: naturalHeight });
      }
    },
    []
  );

  React.useEffect(() => {
    const total = campaigns.length;

    if (total === 0) {
      if (currentIndex !== 0) {
        setCurrentIndex(0);
      }
      return;
    }

    if (currentIndex >= total) {
      setCurrentIndex(0);
    }
  }, [campaigns.length, currentIndex]);

  const handleShowPrevious = React.useCallback(() => {
    setCurrentIndex(previousIndex => {
      if (totalCampaigns <= 1) {
        return previousIndex;
      }

      return previousIndex === 0 ? totalCampaigns - 1 : previousIndex - 1;
    });
  }, [totalCampaigns]);

  const handleShowNext = React.useCallback(() => {
    setCurrentIndex(previousIndex => {
      if (totalCampaigns <= 1) {
        return previousIndex;
      }

      return (previousIndex + 1) % totalCampaigns;
    });
  }, [totalCampaigns]);

  const navigationTarget = React.useMemo(() => {
    if (!currentCampaign?.page) {
      return null;
    }

    const trimmed = currentCampaign.page.trim();
    if (!trimmed) {
      return null;
    }

    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  }, [currentCampaign]);

  const handleNavigateToPage = React.useCallback(() => {
    if (!navigationTarget) {
      return;
    }

    handleClose();
    navigate(navigationTarget);
  }, [handleClose, navigate, navigationTarget]);

  if (!currentCampaign) {
    return null;
  }

  const displayTitle = currentCampaign.title && currentCampaign.title.trim().length > 0
    ? currentCampaign.title.trim()
    : 'Campaña destacada para tu comunidad';

  const canNavigate = totalCampaigns > 1;

  if (!isOpen) return null;

  return (
    <div className="border border-primary/20 bg-app-panel p-5 shadow-sm rounded-none my-4 w-full animate-in fade-in duration-200">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center border border-primary/20 bg-primary/10 text-primary">
            <Megaphone className="h-5 w-5" />
          </span>
          <div className="flex flex-1 flex-col">
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="w-fit bg-card/70 text-primary">
                Campaña activa
              </Badge>
              <Button variant="ghost" size="icon" className="h-7 w-7 -mt-1" onClick={() => handleOpenChange(false)} aria-label="Descartar campaña">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <h3 className="text-lg font-semibold text-foreground font-serif">
                {displayTitle}
              </h3>
              {navigationTarget ? (
                <Button
                  type="button"
                  onClick={handleNavigateToPage}
                  className="ml-auto"
                >
                  Visitar
                </Button>
              ) : null}
            </div>
          </div>
        </div>

        <p className="text-sm text-muted-foreground whitespace-pre-line">
          {currentCampaign.message}
        </p>

        {campaignImageUrl ? (
          <div
            className="flex w-full items-center justify-center overflow-hidden rounded-md border border-border bg-background/70"
            style={campaignImageAspectRatio ? { aspectRatio: campaignImageAspectRatio, maxHeight: '70vh' } : { maxHeight: '70vh' }}
          >
            <img
              src={campaignImageUrl}
              alt={displayTitle}
              className="h-full w-full object-contain"
              loading="lazy"
              onLoad={handleCampaignImageLoad}
            />
          </div>
        ) : null}

        <div className="flex items-center justify-between border-t border-border pt-4">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleShowPrevious}
            disabled={!canNavigate}
            aria-label="Campaña anterior"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex flex-1 items-center justify-center text-sm font-medium text-muted-foreground">
            {isFetching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : canNavigate ? (
              <span>
                {safeIndex + 1} / {totalCampaigns}
              </span>
            ) : null}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleShowNext}
            disabled={!canNavigate}
            aria-label="Siguiente campaña"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};
